import { FC, useState } from 'react';
import { Button, Modal, Select } from 'rizzui';
import { toast } from 'sonner';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useUnlinkedHeadquarters from '../../../../../hooks/api-calls/useUnlinkedHeadquarters';
import { RouteService } from '../../../../../services/route/routeService';
import RequiredInput from '../../../../../components/forms/InputHOC';
import useHandleErrors from '../../../../../hooks/useHandleErrors';

interface AddHeadquarterToRouteModalProps {
    isOpen: boolean;
    onClose: () => void;
    routeId: string;
    onSuccess: () => void;
}

const schema = yup.object({
    selectedHeadquarter: yup.object()
        .shape({
            value: yup.string().required(),
            label: yup.string().required()
        })
        .nullable()
        .required('Por favor selecciona una sede'),
    orderIndex: yup.string()
        .notRequired()
        .test('is-number', 'Debe ser un número válido', (value) => {
            if (!value) return true;
            const num = parseInt(value);
            return !isNaN(num) && num >= 0;
        }),
});

const AddHeadquarterToRouteModal: FC<AddHeadquarterToRouteModalProps> = ({
    isOpen,
    onClose,
    routeId,
    onSuccess
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { unlinkedHeadquarters, loading: headquartersLoading } = useUnlinkedHeadquarters();
    const service = new RouteService();
    const { handleErrors } = useHandleErrors();
    const headquarterOptions = !headquartersLoading && unlinkedHeadquarters.length === 0
        ? [{
            value: '',
            label: 'No hay sedes no vinculadas a ninguna ruta',
            disabled: true,
        }]
        : unlinkedHeadquarters;

    // FORMIK
    const formik = useFormik({
        initialValues: {
            selectedHeadquarter: null,
            orderIndex: '',
        },
        validationSchema: schema,
        validateOnBlur: true,
        onSubmit: values => {
            handleSubmit(values);
        },
    });

    const handleSubmit = async (values: any) => {
        setIsSubmitting(true);

        try {
            const response = await service.addHeadquarterToRoute(
                routeId, 
                values.selectedHeadquarter.value, 
                parseInt(values.orderIndex ?? 999)
            );

            const responseData = response?.getResponseData();

            if (responseData && responseData.success) {
                toast.success('Sede añadida exitosamente a la ruta');
                onSuccess();
                handleClose();
            } else {
                handleErrors(responseData);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg">
            <div className="p-6">
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Añadir Sede a la Ruta
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Selecciona una sede para añadir a esta ruta
                    </p>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sede <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={formik.values.selectedHeadquarter}
                            onChange={(value) => formik.setFieldValue('selectedHeadquarter', value)}
                            options={headquarterOptions}
                            placeholder="Selecciona una sede"
                            disabled={isSubmitting || headquartersLoading}
                            error={formik.touched.selectedHeadquarter && formik.errors.selectedHeadquarter ? String(formik.errors.selectedHeadquarter) : ''}
                        />
                    </div>

                    <div>
                        <RequiredInput
                            required={false}
                            id="orderIndex"
                            label="Orden"
                            type="number"
                            value={formik.values.orderIndex}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Ej: 1, 2, 3..."
                            disabled={isSubmitting}
                            min="0"
                            error={formik.touched.orderIndex && formik.errors.orderIndex ? String(formik.errors.orderIndex) : ''}
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => formik.handleSubmit()}
                        isLoading={isSubmitting}
                        disabled={!formik.values.selectedHeadquarter || !formik.isValid}
                    >
                        Añadir Sede
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default AddHeadquarterToRouteModal;
